/**
 * One team's lineup for a COMPLETED week, read from the record (nfl_player_match_scores for
 * that team and week, including players dropped since) -- the same rule NFLMatchupDetail uses.
 * See lib/nflWeekLineup.ts for the row kinds and why the current roster is never a fallback.
 *
 * `completed` comes from the team's own nfl_matchups row for that week. Anything not completed
 * (the live week, or a week with no matchup) returns completed=false, and callers keep their
 * live path. A completed week with no score rows returns lineup.status 'not-recorded'.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { buildPastWeekLineup, type PastWeekLineup, type WeekScoreRow } from '@/lib/nflWeekLineup';
import type { SlotEligibilityRow } from '@/lib/nflStartablePositions';
import type { GameContext } from '@/lib/nflGameLine';

export interface PastWeekPlayer {
  full_name: string;
  position: string;
  teamAlias: string | null;
  byeWeek: number | null;
  headshot_url: string | null;
  status: string;
  injury_status: string | null;
  /** That WEEK's game for the player's franchise -- not the current week's. */
  game: GameContext | null;
  isBye: boolean;
}

export interface PastWeekData {
  completed: boolean;
  /** The team's stored total for the week (nfl_matchups), the number standings use. */
  storedTotal: number | null;
  lineup: PastWeekLineup | null;
  /** Display info per nfl_player_match_scores row id. */
  info: Map<string, PastWeekPlayer>;
}

export function usePastWeekLineup(args: {
  leagueId: string | null | undefined;
  teamId: string | null | undefined;
  week: number | null | undefined;
  seasonYear: number | null | undefined;
  slotOrder: readonly string[];
  enabled?: boolean;
}) {
  const { leagueId, teamId, week, seasonYear, slotOrder, enabled = true } = args;
  return useQuery({
    queryKey: ['nfl-past-week-lineup', leagueId, teamId, week, seasonYear, slotOrder.join(',')],
    enabled: enabled && !!leagueId && !!teamId && !!week && !!seasonYear,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PastWeekData> => {
      // `as any`: the chained .eq()s tip the generated types past TS2589 (as every nfl_* read here).
      const { data: matchups, error: mErr } = await (supabase as any)
        .from('nfl_matchups')
        .select('home_team_id, away_team_id, home_score, away_score, status')
        .eq('league_id', leagueId)
        .eq('week', week)
        .eq('season_year', seasonYear)
        .eq('season_type', 'REG')
        .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`);
      if (mErr) throw mErr;
      const m = (matchups ?? [])[0] as { home_team_id: string; home_score: number | null; away_score: number | null; status: string } | undefined;
      const completed = !!m && (m.status === 'completed' || m.status === 'final');
      if (!completed) return { completed: false, storedTotal: null, lineup: null, info: new Map() };
      const storedTotal = m!.home_team_id === teamId ? m!.home_score : m!.away_score;

      const [rowsRes, eligRes] = await Promise.all([
        (supabase as any).from('nfl_player_match_scores')
          .select(`id, player_id, is_starter, position_slot, position_slot_source, trash_points, anti_points, sabotage_points,
            nfl_players ( full_name, position, headshot_url, nfl_franchise_id, status, injury_status )`)
          .eq('league_id', leagueId)
          .eq('fantasy_team_id', teamId)
          .eq('week', week)
          .eq('season_year', seasonYear)
          .eq('season_type', 'REG'),
        supabase.from('nfl_slot_eligibility' as never).select('position, slot_base'),
      ]);
      if (rowsRes.error) throw rowsRes.error;
      const rows = (rowsRes.data ?? []) as any[];
      const weekRows: WeekScoreRow[] = rows.map((r) => ({
        id: r.id, player_id: r.player_id, position: r.nfl_players?.position ?? null,
        is_starter: !!r.is_starter, position_slot: r.position_slot ?? null,
        position_slot_source: r.position_slot_source ?? null,
        trash_points: r.trash_points, anti_points: r.anti_points, sabotage_points: r.sabotage_points,
        full_name: r.nfl_players?.full_name ?? null,
      }));
      const lineup = buildPastWeekLineup(weekRows, slotOrder, (eligRes.data ?? []) as unknown as SlotEligibilityRow[]);

      // That week's games and every alias (the opponent is rarely on this roster).
      const [franchisesRes, gamesRes] = await Promise.all([
        supabase.from('nfl_franchises').select('id, alias, bye_week_2026'),
        (supabase as any).from('nfl_games')
          .select('home_franchise_id, away_franchise_id, scheduled, status')
          .eq('season_year', seasonYear).eq('season_type', 'REG').eq('week', week),
      ]);
      const alias: Record<string, string> = {};
      const bye: Record<string, number | null> = {};
      for (const f of (franchisesRes.data ?? []) as unknown as { id: string; alias: string | null; bye_week_2026: number | null }[]) {
        alias[f.id] = f.alias || '';
        bye[f.id] = f.bye_week_2026 ?? null;
      }
      const games: Record<string, GameContext> = {};
      for (const g of (gamesRes.data ?? []) as any[]) {
        games[g.home_franchise_id] = { scheduled: g.scheduled, status: g.status, opponentAlias: alias[g.away_franchise_id] || null, isHome: true };
        games[g.away_franchise_id] = { scheduled: g.scheduled, status: g.status, opponentAlias: alias[g.home_franchise_id] || null, isHome: false };
      }
      const info = new Map<string, PastWeekPlayer>();
      for (const r of rows) {
        const p = r.nfl_players ?? {};
        const f = p.nfl_franchise_id as string | undefined;
        info.set(r.id, {
          full_name: p.full_name ?? 'Unknown', position: p.position ?? '??',
          teamAlias: f ? (alias[f] || null) : null, byeWeek: f ? (bye[f] ?? null) : null,
          headshot_url: p.headshot_url ?? null, status: p.status ?? 'ACT', injury_status: p.injury_status ?? null,
          game: f ? (games[f] ?? null) : null, isBye: f ? !games[f] : false,
        });
      }
      return { completed: true, storedTotal: storedTotal ?? null, lineup, info };
    },
  });
}
